import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEAVE_TYPES = ["Casual", "Sick", "Earned", "Other"];
const MONTHS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const REVIEW_FILTERS = ["all", "pending", "approved", "rejected"];

const STATUS_STYLES = {
  pending: "border-[#B8997A] text-[#8a6f52] bg-[#B8997A]/10",
  approved: "border-[#0B0F14]/50 text-[#0B0F14] bg-[#0B0F14]/5",
  rejected: "border-destructive/50 text-destructive bg-destructive/5",
};

const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const StatusBadge = ({ status }) => (
  <Badge variant="outline" className={`rounded-none uppercase tracking-wider text-[10px] ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {status}
  </Badge>
);

const MyList = ({ items, kind }) => (
  <div className="space-y-px bg-border border border-foreground/10" data-testid={`my-${kind}-list`}>
    {items.length === 0 ? (
      <div className="bg-background p-8 text-center text-sm text-muted-foreground" data-testid={`my-${kind}-empty`}>
        No applications yet.
      </div>
    ) : (
      items.map((r) => (
        <div key={r.id} className="bg-background p-5" data-testid={`my-${kind}-item-${r.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-medium text-sm">
                {kind === "leave"
                  ? `${r.leave_type} leave · ${r.from_date} → ${r.to_date}`
                  : `INR ${Number(r.amount).toLocaleString("en-IN")} · ${r.repayment_months} months`}
              </p>
              <p className="text-sm text-muted-foreground mt-1">{kind === "leave" ? r.reason : r.purpose}</p>
              <p className="text-xs text-muted-foreground/70 mt-2">Submitted {fmtDateTime(r.created_at)}</p>
              {r.manager_comment && (
                <p className="mt-3 text-sm border-l-2 border-[#B8997A] pl-3 text-muted-foreground italic" data-testid={`my-${kind}-comment-${r.id}`}>
                  "{r.manager_comment}" — {r.decided_by}
                </p>
              )}
            </div>
            <StatusBadge status={r.status} />
          </div>
        </div>
      ))
    )}
  </div>
);

const ReviewList = ({ items, kind, onDecide }) => {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? items : items.filter((r) => r.status === filter);
  const count = (f) => (f === "all" ? items.length : items.filter((r) => r.status === f).length);

  return (
    <div className="mt-12" data-testid={`review-${kind}-section`}>
      <h3 className="text-xs uppercase tracking-[0.3em] text-[#B8997A] mb-4">All Applications — Manager Review</h3>
      <div className="flex flex-wrap gap-2 mb-4" data-testid={`review-${kind}-filters`}>
        {REVIEW_FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`review-${kind}-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 text-xs uppercase tracking-wider border transition-colors duration-300 ${
              filter === f
                ? "bg-foreground text-background border-foreground"
                : "border-foreground/20 text-muted-foreground hover:border-[#B8997A] hover:text-[#B8997A]"
            }`}
          >
            {f} ({count(f)})
          </button>
        ))}
      </div>
      <div className="space-y-px bg-border border border-foreground/10">
        {visible.length === 0 ? (
          <div className="bg-background p-8 text-center text-sm text-muted-foreground">No applications.</div>
        ) : (
          visible.map((r) => (
            <div key={r.id} className="bg-background p-5" data-testid={`review-${kind}-item-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <p className="font-medium text-sm">
                    {r.applicant_name} <span className="text-muted-foreground font-normal">({r.applicant_email})</span>
                  </p>
                  <p className="text-sm mt-1">
                    {kind === "leave"
                      ? `${r.leave_type} leave · ${r.from_date} → ${r.to_date}`
                      : `INR ${Number(r.amount).toLocaleString("en-IN")} · ${r.repayment_months} months`}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">{kind === "leave" ? r.reason : r.purpose}</p>
                  {kind === "loan" && r.remarks && <p className="text-xs text-muted-foreground mt-1">Remarks: {r.remarks}</p>}
                  <p className="text-xs text-muted-foreground/70 mt-2">Submitted {fmtDateTime(r.created_at)}</p>
                  {r.manager_comment && (
                    <p className="mt-2 text-sm italic text-muted-foreground">"{r.manager_comment}" — {r.decided_by}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <StatusBadge status={r.status} />
                  {r.status === "pending" && (
                    <>
                      <button
                        onClick={() => onDecide(kind, r, "approved")}
                        data-testid={`review-approve-${r.id}`}
                        className="inline-flex items-center gap-1.5 border border-foreground/25 px-3 py-1.5 text-xs uppercase tracking-wider hover:border-[#0B0F14] hover:bg-[#0B0F14] hover:text-[#F7F5F0] transition-colors"
                      >
                        <Check size={12} strokeWidth={1.5} /> Approve
                      </button>
                      <button
                        onClick={() => onDecide(kind, r, "rejected")}
                        data-testid={`review-reject-${r.id}`}
                        className="inline-flex items-center gap-1.5 border border-foreground/25 px-3 py-1.5 text-xs uppercase tracking-wider hover:border-destructive hover:text-destructive transition-colors"
                      >
                        <X size={12} strokeWidth={1.5} /> Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default function PortalRequests() {
  usePageMeta("Leave & Loans — SKN Group Portal", "Submit and track leave and loan applications.");
  const { user } = useAuth();
  const isManager = user?.is_manager;

  const [myLeave, setMyLeave] = useState([]);
  const [myLoans, setMyLoans] = useState([]);
  const [allLeave, setAllLeave] = useState([]);
  const [allLoans, setAllLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [leaveForm, setLeaveForm] = useState({ leave_type: "", from_date: "", to_date: "", reason: "" });
  const [loanForm, setLoanForm] = useState({ amount: "", purpose: "", repayment_months: "", remarks: "" });
  const [leaveErrors, setLeaveErrors] = useState({});
  const [loanErrors, setLoanErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [decision, setDecision] = useState(null); // { kind, record, status }
  const [comment, setComment] = useState("");
  const [deciding, setDeciding] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const calls = [
        axios.get(`${API}/leave-applications/mine`, { withCredentials: true }),
        axios.get(`${API}/loan-applications/mine`, { withCredentials: true }),
      ];
      if (isManager) {
        calls.push(axios.get(`${API}/leave-applications`, { withCredentials: true }));
        calls.push(axios.get(`${API}/loan-applications`, { withCredentials: true }));
      }
      const res = await Promise.all(calls);
      setMyLeave(res[0].data);
      setMyLoans(res[1].data);
      if (isManager) {
        setAllLeave(res[2].data);
        setAllLoans(res[3].data);
      }
    } catch (err) {
      console.error("Failed to load applications", err);
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const submitLeave = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!leaveForm.leave_type) errs.leave_type = "Select a leave type.";
    if (!leaveForm.from_date) errs.from_date = "Required.";
    if (!leaveForm.to_date) errs.to_date = "Required.";
    else if (leaveForm.from_date && leaveForm.to_date < leaveForm.from_date) errs.to_date = "Must be on or after the from date.";
    if (!leaveForm.reason.trim()) errs.reason = "Please enter a reason.";
    setLeaveErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leave-applications`, { ...leaveForm, reason: leaveForm.reason.trim() }, { withCredentials: true });
      toast.success("Leave application submitted.");
      setLeaveForm({ leave_type: "", from_date: "", to_date: "", reason: "" });
      fetchAll();
    } catch (err) {
      console.error("Failed to submit leave application", err);
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLoan = async (e) => {
    e.preventDefault();
    const errs = {};
    const amount = parseFloat(loanForm.amount);
    if (!loanForm.amount || isNaN(amount) || amount <= 0) errs.amount = "Enter a positive amount.";
    if (!loanForm.purpose.trim()) errs.purpose = "Please enter the purpose.";
    const months = parseInt(loanForm.repayment_months, 10);
    if (!loanForm.repayment_months || isNaN(months) || months < 1 || months > 12) errs.repayment_months = "Between 1 and 12 months.";
    setLoanErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/loan-applications`,
        { amount, purpose: loanForm.purpose.trim(), repayment_months: months, remarks: loanForm.remarks.trim() || null },
        { withCredentials: true }
      );
      toast.success("Loan application submitted.");
      setLoanForm({ amount: "", purpose: "", repayment_months: "", remarks: "" });
      fetchAll();
    } catch (err) {
      console.error("Failed to submit loan application", err);
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openDecision = (kind, record, status) => {
    setDecision({ kind, record, status });
    setComment("");
  };

  const confirmDecision = async () => {
    if (!decision) return;
    setDeciding(true);
    try {
      const path = decision.kind === "leave" ? "leave-applications" : "loan-applications";
      await axios.patch(
        `${API}/${path}/${decision.record.id}/decision`,
        { status: decision.status, comment: comment.trim() || null },
        { withCredentials: true }
      );
      toast.success(`Application ${decision.status}.`);
      setDecision(null);
      fetchAll();
    } catch (err) {
      console.error("Failed to record decision", err);
      toast.error("Could not record the decision.");
    } finally {
      setDeciding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="requests-loading">
        <Loader2 size={26} className="animate-spin bronze" />
      </div>
    );
  }

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen" data-testid="portal-requests-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <Link to="/portal" data-testid="requests-back-link" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#B8997A] transition-colors">
          <ArrowLeft size={14} strokeWidth={1.5} /> Back to Portal
        </Link>
        <h1 className="mt-6 font-display text-4xl md:text-5xl font-medium tracking-tight">
          Leave <span className="italic bronze">&amp; Loans</span>
        </h1>

        <Tabs defaultValue="leave" className="mt-10">
          <TabsList className="bg-secondary rounded-none">
            <TabsTrigger value="leave" data-testid="tab-leave" className="rounded-none text-xs uppercase tracking-wider">Leave</TabsTrigger>
            <TabsTrigger value="loan" data-testid="tab-loan" className="rounded-none text-xs uppercase tracking-wider">Loan</TabsTrigger>
          </TabsList>

          {/* LEAVE */}
          <TabsContent value="leave" className="mt-8">
            <form onSubmit={submitLeave} noValidate className="border border-foreground/15 p-6 md:p-8 space-y-6" data-testid="leave-form">
              <h2 className="font-display text-2xl font-medium tracking-tight">Apply for Leave</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Type</Label>
                  <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({ ...leaveForm, leave_type: v })}>
                    <SelectTrigger data-testid="leave-select-type" className="bg-transparent border-foreground/25">
                      <SelectValue placeholder="Leave type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F7F5F0]">
                      {LEAVE_TYPES.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`leave-type-${t.toLowerCase()}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {leaveErrors.leave_type && <p className="text-sm text-destructive" data-testid="leave-error-type">{leaveErrors.leave_type}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">From</Label>
                  <Input type="date" data-testid="leave-input-from" value={leaveForm.from_date} onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} className="bg-transparent border-foreground/25" />
                  {leaveErrors.from_date && <p className="text-sm text-destructive" data-testid="leave-error-from">{leaveErrors.from_date}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">To</Label>
                  <Input type="date" data-testid="leave-input-to" value={leaveForm.to_date} onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} className="bg-transparent border-foreground/25" />
                  {leaveErrors.to_date && <p className="text-sm text-destructive" data-testid="leave-error-to">{leaveErrors.to_date}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Reason</Label>
                <Textarea data-testid="leave-input-reason" value={leaveForm.reason} onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} rows={3} className="bg-transparent border-foreground/25 resize-none" />
                {leaveErrors.reason && <p className="text-sm text-destructive" data-testid="leave-error-reason">{leaveErrors.reason}</p>}
              </div>
              <button type="submit" disabled={submitting} data-testid="leave-submit-button" className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60">
                {submitting && <Loader2 size={14} className="animate-spin" />} Submit Leave Application
              </button>
            </form>

            <h3 className="mt-10 mb-4 text-xs uppercase tracking-[0.3em] text-[#B8997A]">My Applications</h3>
            <MyList items={myLeave} kind="leave" />
            {isManager && <ReviewList items={allLeave} kind="leave" onDecide={openDecision} />}
          </TabsContent>

          {/* LOAN */}
          <TabsContent value="loan" className="mt-8">
            <form onSubmit={submitLoan} noValidate className="border border-foreground/15 p-6 md:p-8 space-y-6" data-testid="loan-form">
              <h2 className="font-display text-2xl font-medium tracking-tight">Apply for a Loan</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Amount (INR)</Label>
                  <Input type="number" min="1" data-testid="loan-input-amount" value={loanForm.amount} onChange={(e) => setLoanForm({ ...loanForm, amount: e.target.value })} placeholder="e.g., 50000" className="bg-transparent border-foreground/25" />
                  {loanErrors.amount && <p className="text-sm text-destructive" data-testid="loan-error-amount">{loanErrors.amount}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Purpose</Label>
                  <Input data-testid="loan-input-purpose" value={loanForm.purpose} onChange={(e) => setLoanForm({ ...loanForm, purpose: e.target.value })} placeholder="Purpose of the loan" className="bg-transparent border-foreground/25" />
                  {loanErrors.purpose && <p className="text-sm text-destructive" data-testid="loan-error-purpose">{loanErrors.purpose}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em]">Repayment (months)</Label>
                  <Select value={loanForm.repayment_months} onValueChange={(v) => setLoanForm({ ...loanForm, repayment_months: v })}>
                    <SelectTrigger data-testid="loan-select-months" className="bg-transparent border-foreground/25">
                      <SelectValue placeholder="1–12 months" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F7F5F0]">
                      {MONTHS.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`loan-months-${m}`}>{m} {m === "1" ? "month" : "months"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {loanErrors.repayment_months && <p className="text-sm text-destructive" data-testid="loan-error-months">{loanErrors.repayment_months}</p>}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Remarks <span className="text-muted-foreground normal-case">(optional)</span></Label>
                <Textarea data-testid="loan-input-remarks" value={loanForm.remarks} onChange={(e) => setLoanForm({ ...loanForm, remarks: e.target.value })} rows={2} className="bg-transparent border-foreground/25 resize-none" />
              </div>
              <button type="submit" disabled={submitting} data-testid="loan-submit-button" className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60">
                {submitting && <Loader2 size={14} className="animate-spin" />} Submit Loan Application
              </button>
            </form>

            <h3 className="mt-10 mb-4 text-xs uppercase tracking-[0.3em] text-[#B8997A]">My Applications</h3>
            <MyList items={myLoans} kind="loan" />
            {isManager && <ReviewList items={allLoans} kind="loan" onDecide={openDecision} />}
          </TabsContent>
        </Tabs>

        {/* Decision dialog */}
        <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
          <DialogContent className="bg-[#F7F5F0] max-w-md" data-testid="decision-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl capitalize">
                {decision?.status === "approved" ? "Approve" : "Reject"} application
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-2">
              <p className="text-sm text-muted-foreground">
                {decision?.record?.applicant_name} — {decision?.kind === "leave"
                  ? `${decision?.record?.leave_type} leave, ${decision?.record?.from_date} → ${decision?.record?.to_date}`
                  : `INR ${Number(decision?.record?.amount || 0).toLocaleString("en-IN")}, ${decision?.record?.repayment_months} months`}
              </p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em]">Comment <span className="text-muted-foreground normal-case">(optional)</span></Label>
                <Textarea data-testid="decision-input-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="bg-transparent border-foreground/25 resize-none" />
              </div>
              <button
                onClick={confirmDecision}
                disabled={deciding}
                data-testid="decision-confirm-button"
                className="inline-flex items-center gap-2 bg-foreground text-background px-8 py-3 text-sm font-medium hover:bg-[#B8997A] hover:text-[#0B0F14] transition-colors disabled:opacity-60"
              >
                {deciding && <Loader2 size={14} className="animate-spin" />}
                Confirm {decision?.status === "approved" ? "Approval" : "Rejection"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
